import ExtraResponse from "@/model/ExtraResponse";
import PropertyResponse from "@/model/PropertyResponse";
import StayQuote from "@/model/StayQuote";
import isoDate from "@/utils/isoDate";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Nights between two ISO dates (the check-out day is not a night). 0 when the range is invalid. */
function computeNights(checkIn: string, checkOut: string): number {
  if (!checkIn || !checkOut) {
    return 0;
  }
  const nights = Math.round(
    (isoDate.parseIsoDate(checkOut).getTime() - isoDate.parseIsoDate(checkIn).getTime()) /
      MS_PER_DAY
  );
  return Math.max(0, nights);
}

/** Same multipliers as the backend's BookingMapper. */
function computeExtraTotal(extra: ExtraResponse, nights: number, guests: number): number {
  const price = extra.priceAmount ?? 0;
  switch (extra.pricingUnit) {
    case "PER_NIGHT":
      return price * nights;
    case "PER_GUEST":
      return price * guests;
    case "PER_GUEST_PER_NIGHT":
      return price * nights * guests;
    default:
      return price;
  }
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function computeStayQuote(
  property: PropertyResponse,
  nights: number,
  guests: number,
  extras: ExtraResponse[] = []
): StayQuote {
  const nightlyPrice = property.nightlyPriceAmount ?? 0;
  const cleaningFee = property.cleaningFeeAmount ?? 0;
  const lodgingTotal = nightlyPrice * nights;
  const extraLines = extras.map((extra) => ({
    extraId: extra.id ?? "",
    name: extra.name ?? "",
    total: roundMoney(computeExtraTotal(extra, nights, guests)),
  }));

  return {
    nights,
    nightlyPrice,
    lodgingTotal: roundMoney(lodgingTotal),
    cleaningFee,
    extras: extraLines,
    total: roundMoney(
      lodgingTotal + cleaningFee + extraLines.reduce((sum, line) => sum + line.total, 0)
    ),
    currency: property.priceCurrency ?? "EUR",
  };
}

function formatMoney(amount: number | undefined, currency: string | undefined, locale?: string): string {
  if (amount === undefined || amount === null) {
    return "—";
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency ?? "EUR",
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

export { computeNights, computeExtraTotal, computeStayQuote, formatMoney };
